import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, FileText, BarChart2 } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { FilterBar } from "@/components/FilterBar";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const REPORTS = [
  { value: "lead_source", label: "Lead Source" },
  { value: "lead_category", label: "Lead Category" },
  { value: "lead_generator", label: "Lead Generator" },
  { value: "sales_performance", label: "Sales Performance" },
  { value: "login", label: "Login" },
  { value: "user_activity", label: "User Activity" },
  { value: "lead_access", label: "Lead Access" },
  { value: "follow_up", label: "Follow-Up" },
  { value: "conversion", label: "Conversion" },
];

// Configuration of which key to use for x-axis and y-bar per chartable report
const CHART_CONFIG = {
  lead_source: { x: "source", y: ["leads", "won"] },
  lead_category: { x: "category", y: ["leads", "won"] },
  lead_generator: { x: "generator", y: ["leads"] },
  sales_performance: { x: "executive", y: ["leads_assigned", "won"] },
  conversion: { x: "stage", y: ["count"] },
};

export default function Reports() {
  const [type, setType] = useState("lead_source");
  const [filters, setFilters] = useState({});
  const [data, setData] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(false);

  const params = () => {
    const p = {};
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to + "T23:59:59";
    return p;
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${type}`, { params: params() });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setData({ rows: [], count: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [type, filters]);

  const exportFile = async (fmt) => {
    try {
      const token = localStorage.getItem("crm_token");
      const url = fmt === "xlsx"
        ? `${API}/reports/${type}/xlsx?${new URLSearchParams(params()).toString()}`
        : `${API}/reports/${type}?${new URLSearchParams({ ...params(), format: "csv" }).toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${type}.${fmt}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed"); }
  };

  const columns = data.rows[0] ? Object.keys(data.rows[0]) : [];
  const chartCfg = CHART_CONFIG[type];

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="reports-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-label">Insights</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold mt-1">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">{data.count} rows · {REPORTS.find((r) => r.value === type)?.label} Report</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportFile("csv")} disabled={!data.rows.length}
            data-testid="export-csv-btn"
            className="text-sm font-medium rounded-sm py-2 px-3 border border-border hover:bg-secondary disabled:opacity-50 flex items-center gap-1.5">
            <FileText className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => exportFile("xlsx")} disabled={!data.rows.length}
            data-testid="export-xlsx-btn"
            className="text-white text-sm font-medium rounded-sm py-2 px-3 hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
            style={{ backgroundColor: "var(--theme-color)" }}>
            <Download className="w-4 h-4" /> Excel
          </button>
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        {REPORTS.map((r) => (
          <button key={r.value} onClick={() => setType(r.value)}
            data-testid={`report-tab-${r.value}`}
            className={
              "text-sm px-3 py-1.5 rounded-sm border transition-colors " +
              (type === r.value
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary")
            }>
            {r.label}
          </button>
        ))}
      </div>

      <FilterBar value={filters} onChange={setFilters} enabled={["date_range"]} />

      {/* Chart */}
      {chartCfg && data.rows.length > 0 && (
        <section className="border border-border rounded-sm bg-background p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Visualization</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.rows.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey={chartCfg.x} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--background))",
                  border: "1px solid hsl(var(--border))",
                  fontSize: 12, borderRadius: 2,
                }}
              />
              {chartCfg.y.map((k, i) => (
                <Bar key={k} dataKey={k}
                  fill={i === 0 ? "var(--theme-color)" : "#10b981"}
                  radius={[2, 2, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* Table */}
      <section className="border border-border rounded-sm bg-background overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr>{columns.map((c) => (
                <th key={c} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {c.replace(/_/g, " ")}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={columns.length || 1} className="text-center py-10 text-muted-foreground">Loading…</td></tr>}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={1} className="text-center py-12">
                  <FileSpreadsheet className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <div className="text-sm text-muted-foreground">No data for the selected filters.</div>
                </td></tr>
              )}
              {data.rows.map((row, i) => (
                <tr key={i} className="border-t border-border">
                  {columns.map((c) => (
                    <td key={c} className="px-4 py-3">
                      {row[c] == null || row[c] === "" ? <span className="text-muted-foreground">—</span> :
                       (typeof row[c] === "object" ? JSON.stringify(row[c]) : String(row[c]))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
